import { useState } from 'react'
import {
  Archive, ArchiveRestore, ArrowLeftToLine, ArrowRightToLine, Copy, Pencil, Pin, PinOff, Repeat, Trash2,
} from 'lucide-react'
import { Button, Field, Row, Sheet, useToast } from '../ui'
import {
  useArchiveList, useChangeListKind, useDeleteList, useDuplicateList, usePinList, useRenameList, useReorderLists,
} from '../lib/mutations'
import { useListItems, useLists } from '../lib/queries'
import { chipOrder } from '../lib/chipOrder'
import { fillSlots, moveToEdge } from '../lib/reorder'
import type { List } from '../lib/types'
import { KindPicker } from './KindPicker'

/** Matches the toast's own default lifetime (`ui/Toast.tsx`'s `DEFAULT_MS`),
 *  which is not exported — this file keeps its own name for the same number
 *  so the delete timer and the toast it rides on can never drift apart. */
const UNDO_MS = 5000

/**
 * L9: rename, change kind, archive/unarchive, delete — plus pin, which the
 * index draws as its own group rather than a menu item elsewhere. Long-press
 * on a list row (or its trailing `more-horizontal`) opens this.
 *
 * Built the way `EventActions` is: a `Sheet` of `Row`s, because that is the
 * one thing in the app a long press opens and there is no reason for a
 * second shape of it.
 */
export function ListMenu({ list, onClose }: { list: List; onClose: () => void }) {
  const [view, setView] = useState<'menu' | 'rename' | 'kind' | 'duplicate'>('menu')
  const [name, setName] = useState(list.name)
  const [dupName, setDupName] = useState(`${list.name} copy`)
  const pinM = usePinList()
  const renameM = useRenameList()
  const kindM = useChangeListKind()
  const archiveM = useArchiveList()
  const deleteM = useDeleteList()
  const dupM = useDuplicateList()
  const reorderM = useReorderLists()
  const listsQ = useLists()
  const toast = useToast()
  // Cached under the same `['list_items']` key `Lists.tsx` and `ListDetail.tsx`
  // already hold — calling the hook again here costs nothing extra and is
  // what lets "Duplicate" copy this list's items without `ListMenu` taking
  // them as a prop (its call site in `Lists.tsx` is outside this package's
  // *Files*).
  const itemsQ = useListItems()
  const items = (itemsQ.data ?? []).filter(i => i.list_id === list.id)

  const archived = !!list.archived_at

  // Where this list is in the chip row, so the menu offers only a move that
  // does something: "Move to start" on the first chip would be a row that
  // does nothing when pressed. An archived list is not a chip and gets neither.
  const allIds = (listsQ.data ?? []).map(l => l.id)
  const chipIds = chipOrder(listsQ.data ?? []).map(l => l.id)
  const toStart = archived ? null : moveToEdge(chipIds, list.id, 'start')
  const toEnd = archived ? null : moveToEdge(chipIds, list.id, 'end')

  /** The reorder a drag does, without a finger: the same mutation, the same
   *  whole-set positions. Undo writes every position back as it was. */
  function move(next: string[], where: 'start' | 'end') {
    const before = allIds
    reorderM.mutate(fillSlots(allIds, next))
    onClose()
    toast({
      text: `Moved "${list.name}" to the ${where}`,
      ms: UNDO_MS,
      action: { label: 'Undo', onClick: () => reorderM.mutate(before) },
    })
  }

  /** Rule 1: every destructive action goes through the toast Undo. Archiving
   *  is reversible by construction (`archived_at = null`), but it still
   *  takes the list out of the active groups on the tap, so the undo matters
   *  exactly as much as it does for a swipe-to-remove row. */
  function archive() {
    archiveM.mutate({ id: list.id, archived: true })
    onClose()
    toast({
      text: `Archived "${list.name}"`,
      ms: UNDO_MS,
      action: { label: 'Undo', onClick: () => archiveM.mutate({ id: list.id, archived: false }) },
    })
  }

  /** Rule 2: delete is archive-then-delete, and nothing is deleted before
   *  the toast expires. The archive happens on the tap so the list leaves
   *  the active groups at once; the real delete is a plain `setTimeout` set
   *  to the same lifetime the toast itself uses, so "the toast expired" and
   *  "the row is actually gone" are the same moment rather than two clocks
   *  that can disagree. */
  function del() {
    archiveM.mutate({ id: list.id, archived: true })
    onClose()
    const timer = setTimeout(() => deleteM.mutate(list.id), UNDO_MS)
    toast({
      text: `Deleted "${list.name}"`,
      ms: UNDO_MS,
      action: {
        label: 'Undo',
        onClick: () => { clearTimeout(timer); archiveM.mutate({ id: list.id, archived: false }) },
      },
    })
  }

  if (view === 'rename') {
    return (
      <Sheet open onOpenChange={o => { if (!o) onClose() }} title="Rename list"
        actions={
          <>
            <Button variant="primary" disabled={!name.trim()}
              onClick={() => { renameM.mutate({ id: list.id, name: name.trim() }); onClose() }}>
              Save
            </Button>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
          </>
        }>
        <Field label="Name">
          <Field.Input value={name} autoFocus onChange={e => setName(e.target.value)} />
        </Field>
      </Sheet>
    )
  }

  if (view === 'kind') {
    return (
      <Sheet open onOpenChange={o => { if (!o) onClose() }} title="Change kind">
        <KindPicker value={list.kind} onChange={kind => { kindM.mutate({ id: list.id, kind }); onClose() }} />
      </Sheet>
    )
  }

  if (view === 'duplicate') {
    return (
      <Sheet open onOpenChange={o => { if (!o) onClose() }} title="Duplicate checklist"
        actions={
          <>
            <Button variant="primary" disabled={!dupName.trim()}
              onClick={() => { dupM.mutate({ list, items, name: dupName.trim() }); onClose() }}>
              Create
            </Button>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
          </>
        }>
        <p className="lists-backbar__label">Copies every room and task. Ticks and run history start fresh.</p>
        <Field label="Name">
          <Field.Input value={dupName} autoFocus onChange={e => setDupName(e.target.value)} />
        </Field>
      </Sheet>
    )
  }

  return (
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title={list.name}>
      <Row
        className="u-row--list"
        leading={list.pinned ? <PinOff size={20} /> : <Pin size={20} />}
        title={list.pinned ? 'Unpin' : 'Pin'}
        meta={null}
        onPress={() => { pinM.mutate({ id: list.id, pinned: !list.pinned }); onClose() }}
      />
      <Row
        className="u-row--list"
        leading={<Pencil size={20} />}
        title="Rename"
        meta={null}
        onPress={() => setView('rename')}
      />
      <Row
        className="u-row--list"
        leading={<Repeat size={20} />}
        title="Change kind"
        meta={null}
        onPress={() => setView('kind')}
      />
      {/* L5's own action: templates only, per the brief's Checklists section.
          A one-off `buy`/`collection` list has nothing a duplicate is for. */}
      {list.kind === 'template' && (
        <Row
          className="u-row--list"
          leading={<Copy size={20} />}
          title="Duplicate"
          meta={null}
          onPress={() => setView('duplicate')}
        />
      )}
      {toStart && (
        <Row className="u-row--list" leading={<ArrowLeftToLine size={20} />} title="Move to start" meta={null}
          onPress={() => move(toStart, 'start')} />
      )}
      {toEnd && (
        <Row className="u-row--list" leading={<ArrowRightToLine size={20} />} title="Move to end" meta={null}
          onPress={() => move(toEnd, 'end')} />
      )}
      {archived ? (
        <Row
          className="u-row--list"
          leading={<ArchiveRestore size={20} />}
          title="Unarchive"
          meta={null}
          onPress={() => { archiveM.mutate({ id: list.id, archived: false }); onClose() }}
        />
      ) : (
        <Row className="u-row--list" leading={<Archive size={20} />} title="Archive" meta={null} onPress={archive} />
      )}
      <Row className="u-row--list" leading={<Trash2 size={20} />} title="Delete" meta={null} onPress={del} />
    </Sheet>
  )
}
